import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { type Blueprint, type EventPayloadAction, useSubscription } from '@rta/rta-blueprint-library';

import { addContextMenu, ReferenceEntryType } from '../../../context-menu';
import { AppContext } from '@r-app/app-context';
import { AppRootContextProvider } from '@r-app/app-root-context-provider';
import { BlueprintSvgContextMenuWrapper } from './blueprint-svg-context-menu-wrapper';
import { ComponentController } from '@r-core/controller';
import { computeFittedViewBox } from './compute-fitted-viewbox';
import { CreateBlueprintSymbols } from '../../symbols';
import { type CustomPanelData, useBlueprintCustomPages } from './blueprint-custom-pages-context';
import { CustomPanelSizeDialog } from './custom-panel-size-dialog';
import { DisplayMode } from '@r-core/controller/enums/display-mode.enum';
import { PANEL_GROUP_SPACING } from '../../render/render-panels';
import { renderPanelDimensions, renderPanelRect } from '../../render/render-panel-base';
import { usePageFontFactorOverride } from './page-font-factor-context';
import { useStabilizedInkExtent } from './use-stabilized-ink-extent.hook';

export type BlueprintCustomPanelCardProps = {
	blueprint: Blueprint;
	/** The blank sheet these panels belong to — also the right-click target id for "Add Panel". */
	pageId: string;
	panels: Array<CustomPanelData>;
};

type PlacedPanel = CustomPanelData & { centerX: number };

/** Empty sheets still need a viewBox wide enough to right-click into. */
const EMPTY_PAGE_EXTENT = { left: -60, right: 60, top: -40, bottom: 0 };

const placePanels = (panels: Array<CustomPanelData>): Array<PlacedPanel> => {
	const totalWidth = panels.reduce((sum, panel) => sum + panel.width, 0) + (PANEL_GROUP_SPACING * Math.max(panels.length - 1, 0));
	let cursor = -totalWidth / 2;

	return panels.map((panel) => {
		const centerX = cursor + (panel.width / 2);
		cursor += panel.width + PANEL_GROUP_SPACING;
		return { ...panel, centerX };
	});
};

/**
 * Draws the free-standing rectangles on a blank "CUSTOM PANEL" sheet. They are plain geometry —
 * no Island, Panel or Item is created — so nothing here reaches pricing, the overhead layout or
 * any other sheet. Multiple panels bottom-align into a horizontal stack with the same gap the
 * side-panels sheet uses (PANEL_GROUP_SPACING).
 */
const BlueprintCustomPanelCardInner = (props: BlueprintCustomPanelCardProps): React.ReactElement => {
	const { blueprint, pageId, panels } = props;

	const mainContext = useContext(AppContext);
	const baseFactor = mainContext?.controller?.options?.dimensionFontSizeFactor ?? 1.2;
	const pageFontFactorOverride = usePageFontFactorOverride();
	const fontFactor = pageFontFactorOverride ?? baseFactor;

	const { addCustomPanel, removeCustomPage, removeCustomPanel, setCustomPanelSize } = useBlueprintCustomPages();

	const svgRef = useRef<SVGSVGElement>(null);
	const contentGroupRef = useRef<SVGGElement>(null);
	const [containerPx, setContainerPx] = useState<{ w: number; h: number }>({ w: 800, h: 400 });
	const [editingPanelId, setEditingPanelId] = useState<string | null>(null);

	// Own controller per sheet, as every other blueprint page does — DimensionLine reads its
	// options and the shared ContextMenu reads its displayMode.
	const controller = useMemo((): ComponentController => {
		const ctrl = new ComponentController(blueprint, { eventGroup: `bp-custom-${pageId}-${blueprint.id}` });
		ctrl.navigation.displayMode = DisplayMode.BlueprintView;
		ctrl.options.isBlueprintView = true;
		ctrl.options.readonly = false;
		ctrl.options.contextMenu = true;
		ctrl.options.backgroundColor = '#FFFFFF';
		ctrl.options.useFractions = mainContext?.controller?.options?.useFractions ?? true;
		ctrl.options.dimensionFontSizeFactor = fontFactor;
		ctrl.options.dimensions = { fontSizeFactor: fontFactor };
		return ctrl;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [blueprint, pageId]);

	if (controller.options.dimensionFontSizeFactor !== fontFactor) {
		controller.options.dimensionFontSizeFactor = fontFactor;
		controller.options.dimensions = { fontSizeFactor: fontFactor };
	}

	// Context-menu items can't reach this component directly, so they publish on the sheet's own
	// controller — which only this sheet listens to.
	const [menuEvent] = useSubscription(controller, false, [['add_custom_panel_action|edit_custom_panel_action|remove_custom_panel_action|remove_custom_panel_page_action', 'requested']]);

	useEffect(() => {
		if (!menuEvent) return;
		const { ref } = menuEvent.payload as EventPayloadAction;

		if (menuEvent.noun === 'add_custom_panel_action') addCustomPanel(pageId);
		if (menuEvent.noun === 'edit_custom_panel_action') setEditingPanelId(ref as string);
		if (menuEvent.noun === 'remove_custom_panel_action') removeCustomPanel(pageId, ref as string);
		if (menuEvent.noun === 'remove_custom_panel_page_action') removeCustomPage(pageId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [menuEvent]);

	useEffect(() => {
		const el = svgRef.current;
		if (!el) return;

		const update = (): void => {
			const rect = el.getBoundingClientRect();
			if (rect.width > 0 && rect.height > 0) setContainerPx({ w: rect.width, h: rect.height });
		};
		update();

		const observer = new ResizeObserver(update);
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		return () => { controller?.destroy(); };
	}, [controller]);

	const placed = useMemo(() => placePanels(panels), [panels]);

	const inkExtent = useStabilizedInkExtent({
		groupRef: contentGroupRef,
		resetKey: `${pageId}|${panels.map((panel) => `${panel.id}:${panel.width}x${panel.height}`).join(',')}|${fontFactor}`
	});

	const geometry = useMemo(() => {
		if (placed.length === 0) return EMPTY_PAGE_EXTENT;
		const halfWidth = (placed[placed.length - 1].centerX + (placed[placed.length - 1].width / 2));
		return {
			left: -halfWidth,
			right: halfWidth,
			top: -Math.max(...placed.map((panel) => panel.height)),
			bottom: 0
		};
	}, [placed]);

	const viewBox = useMemo(
		() => computeFittedViewBox({ geometry, ink: placed.length === 0 ? null : inkExtent, containerPx }),
		[geometry, inkExtent, containerPx, placed.length]
	);
	const [vbX, vbY, vbW, vbH] = viewBox.split(' ').map(Number);

	const editingPanel = panels.find((panel) => panel.id === editingPanelId) ?? null;

	return (
		<AppRootContextProvider controller={controller}>
			<BlueprintSvgContextMenuWrapper svgRef={svgRef}>
				<svg
					ref={svgRef}
					xmlns="http://www.w3.org/2000/svg"
					style={{ width: '100%', height: '100%', display: 'block', background: 'white' }}
					viewBox={viewBox}
					preserveAspectRatio="xMidYMid meet"
				>
					<CreateBlueprintSymbols />

					{/* Right-click target for the empty sheet itself — outside the measured group so it never inflates the fit. */}
					<rect
						x={vbX}
						y={vbY}
						width={vbW}
						height={vbH}
						fill="transparent"
						style={{ pointerEvents: 'all' }}
						{...addContextMenu({ menuKey: ReferenceEntryType.CustomPanelPage, id: pageId })}
					/>

					<g ref={contentGroupRef}>
						{placed.map((panel) => (
							<g key={panel.id} id={`custom-panel-${panel.id}`}>
								{renderPanelRect({
									width: panel.width,
									height: panel.height,
									center: { x: panel.centerX, y: -(panel.height / 2) },
									instanceType: ReferenceEntryType.CustomPanel,
									id: panel.id,
									fill: 'darkgray',
									stroke: 'none'
								})}
								{renderPanelDimensions({
									width: panel.width,
									height: panel.height,
									center: { x: panel.centerX, y: -(panel.height / 2) },
									includeLabelBox: false,
									isFirst: true
								})}
							</g>
						))}
					</g>
				</svg>
			</BlueprintSvgContextMenuWrapper>

			<CustomPanelSizeDialog
				key={editingPanelId ?? 'none'}
				panel={editingPanel}
				onApply={(size) => { if (editingPanel) setCustomPanelSize(pageId, editingPanel.id, size); }}
				onClose={() => setEditingPanelId(null)}
			/>
		</AppRootContextProvider>
	);
};

export const BlueprintCustomPanelCard = React.memo(BlueprintCustomPanelCardInner);
